/**
 * Quantity handling for multi-unit stock — portal twin of backend/utils/qty.js.
 *
 * Kept as a separate file rather than shared, because the portal is a CRA build
 * with no path into backend/. The two must stay in sync; the unit list and the
 * rounding rule are the parts that matter.
 *
 * The portal needs this for the same reason the backend does, plus one of its
 * own: several components compute line totals client-side for display before the
 * server ever sees them (PurchaseInvoiceModal, CreateQuotation), and a
 * `parseInt(quantity)` there shows the customer a different number than the
 * invoice will carry.
 *
 * A note for anyone touching the inputs these feed: `<input type="number">`
 * defaults to `step=1`, and several quantity inputs also carry `min="1"`.
 * Both must change — `step="0.001"` alone still leaves 0.5 failing constraint
 * validation, and the form then refuses to submit with no visible error.
 */

/** The catalogue. Mirrors backend/utils/qty.js — keep in sync. */
export const UNITS = ['piece', 'L', 'mL', 'kg', 'set', 'roll'];

/** Units that cannot be subdivided. Everything else carries 3 decimal places. */
export const DISCRETE_UNITS = new Set(['piece', 'set']);

/** Decimal places for a measured unit — 1 g / 1 mL, matching NUMERIC(12,3). */
export const QTY_SCALE = 3;

/** The `step` attribute a quantity input needs for this unit. */
export const stepFor = (unit = 'piece') => (DISCRETE_UNITS.has(unit) ? '1' : '0.001');

/** True when the unit cannot hold a fraction. */
export const isDiscrete = (unit) => DISCRETE_UNITS.has(unit);

/**
 * Short label for a unit — "L", "kg", "لتر" — for putting beside a number.
 *
 * `pt.unit.*` is the long form the category picker needs ("Litre (L)"); it
 * reads badly as a field suffix. Takes `t` rather than importing i18n so this
 * file stays pure and testable.
 *
 * Returns '' for an unknown unit, so a caller can render nothing at all rather
 * than an empty bracket — several stock screens know the quantity before they
 * know the part.
 */
export const unitLabel = (t, unit) =>
  (unit ? t(`pt.unitShort.${unit}`, { defaultValue: unit }) : '');

/**
 * "Quantity (Litre)" — the label for a quantity input, carrying the unit the
 * number will be read in.
 *
 * This exists because the number was ambiguous at the moment it was typed. The
 * `step` on those inputs already adapts (1 for a piece, 0.001 for a litre), but
 * a step is invisible: a storeman adding oil to a job card saw a box labelled
 * only "Quantity" and had to guess whether 4 meant four bottles or four litres.
 * The unit is shown ALWAYS, including for pieces — a label that appears only
 * sometimes is one the reader stops trusting.
 *
 * Falls back to the bare label when the unit is unknown (a part has not been
 * picked yet), rather than rendering an empty bracket.
 */
export const quantityLabel = (t, unit, key = 'quantity') => {
  const label = t(key);
  const suffix = unitLabel(t, unit);
  return suffix ? `${label} (${suffix})` : label;
};

/**
 * Coerce any quantity representation to a finite Number.
 *
 * Accepts the string an API returns for a DECIMAL, a real Number, or the string
 * an input yields. Returns `fallback` (0) for null, undefined, empty string or
 * anything non-finite — never NaN, which would render as "NaN" in a total.
 *
 * This is the parseInt replacement. It deliberately does not round.
 */
export const qty = (value, fallback = 0) => {
  if (value === null || value === undefined) return fallback;
  // Trim before the emptiness test: Number('   ') is 0, not NaN, so a
  // whitespace-only value used to sail past this guard and come back as a
  // confident zero. Every caller that passes a non-default fallback means "if
  // nothing was given, use this" — qty(quantity, 1), qty(cost_price, null),
  // qty(row.low_stock_threshold, min_stock), qty(movement.quantity_after, stock).
  // A silent 0 there is a free part, a silenced low-stock alert, or a zeroed
  // ledger balance, none of which anything downstream flags.
  const trimmed = typeof value === 'string' ? value.trim() : value;
  if (trimmed === '') return fallback;
  const n = typeof trimmed === 'number' ? trimmed : Number(trimmed);
  return Number.isFinite(n) ? n : fallback;
};

/**
 * Round a quantity the way its unit allows: whole for pieces and sets, 3 places
 * for measured units. Use at boundaries, not between intermediate steps.
 */
/**
 * Sentinel for "this is measured, whatever it is" — used by formatQty so it can
 * ask roundQty for 3-dp behaviour without naming a real unit it does not know.
 */
const MEASURED = '__measured__';

export const roundQty = (value, unit = 'piece') => {
  const n = qty(value);
  if (isDiscrete(unit)) return Math.round(n);
  const factor = 10 ** QTY_SCALE;
  return Math.round(n * factor) / factor;
};

/**
 * Display form: no trailing zeros, and no decimal point on a discrete unit.
 *
 * An UNKNOWN unit is not a piece. The parameter used to default to 'piece',
 * which is discrete, so an unknown unit was rounded to a whole number — and the
 * unit is unknown on the documents customers actually read. `invoice_items` has
 * no `unit` column (only `unit_code`, the Rec 20 snapshot) and `quotation_items`
 * has none at all, yet eight display sites pass `item.unit`. It is undefined at
 * every one of them, so a 2.5 L line printed as "3" and a 0.5 L line as "1" on
 * PublicInvoiceView, PublicQuotationView, ApproveQuotation, QuotationDetails and
 * both preview modals.
 *
 * The money was never wrong — only the printed quantity — which is the bad way
 * round: the customer reads "3 x 20.00 = 50.00" and it does not multiply out.
 * The invoice PDF disagreed too, printing a raw 2.5 for the same line.
 *
 * Defaulting the other way costs nothing. A genuine piece count is STORED whole
 * (NUMERIC(12,3) holding 3.000), so the measured path formats it "3" exactly as
 * the discrete path did. Only fractions change, and for those the stored number
 * is the truth. Rounding stays for a unit explicitly given as piece/set.
 *
 * roundQty keeps its 'piece' default on purpose: it is the WRITE-side helper,
 * and changing what a write rounds to is a different decision from what a
 * screen prints.
 */
export const formatQty = (value, unit) => {
  // Unknown unit => measured. Only an EXPLICIT discrete unit rounds.
  const discrete = unit !== undefined && unit !== null && isDiscrete(unit);
  const n = roundQty(value, discrete ? unit : MEASURED);
  return discrete ? String(n) : String(parseFloat(n.toFixed(QTY_SCALE)));
};
